import {
  buildImageCssFilter,
  IMAGE_FILTER_CONTROL_BOUNDS,
  IMAGE_FILTER_PRESETS,
  type ImageFilterSettings,
} from '@omokage/shared';
import { Button } from '@/components/ui/button';
import type { PresencePhase } from '@/hooks/usePresence';
import { cn } from '@/lib/utils';

interface ImageFilterPanelProps {
  previewSrc: string;
  value: ImageFilterSettings;
  isSaving: boolean;
  isCropOverlayVisible: boolean;
  onChange: (value: ImageFilterSettings) => void;
  onReset: () => void;
  onSave: () => void;
  onDownload?: () => void;
  onToggleCropOverlay: () => void;
  onClose: () => void;
  presencePhase?: PresencePhase;
}

const FILTER_LABELS: Record<keyof ImageFilterSettings, string> = {
  brightness: '明るさ',
  contrast: 'コントラスト',
  saturate: '彩度',
  hueRotate: '色相回転',
  sepia: 'セピア',
  grayscale: 'グレースケール',
  vignette: 'ビネット',
  temperature: '色温度',
  grain: 'フィルムノイズ',
  fade: 'フェード',
};

const FILTER_KEYS = Object.keys(FILTER_LABELS) as Array<keyof ImageFilterSettings>;

function formatFilterValue(key: keyof ImageFilterSettings, value: number): string {
  if (key === 'hueRotate') {
    return `${Math.round(value)}deg`;
  }

  if (
    key === 'sepia' ||
    key === 'grayscale' ||
    key === 'vignette' ||
    key === 'grain' ||
    key === 'fade'
  ) {
    return `${Math.round(value * 100)}%`;
  }

  if (key === 'temperature') {
    if (value > 0) return `+${Math.round(value * 100)}%`;
    if (value < 0) return `${Math.round(value * 100)}%`;
    return '0%';
  }

  return value.toFixed(2);
}

export function ImageFilterPanel({
  previewSrc,
  value,
  isSaving,
  isCropOverlayVisible,
  onChange,
  onReset,
  onSave,
  onDownload,
  onToggleCropOverlay,
  onClose,
  presencePhase = 'entered',
}: ImageFilterPanelProps) {
  return (
    <aside
      data-presence={presencePhase}
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50 flex max-h-[55vh] flex-col border-t border-border bg-card/95 backdrop-blur-md',
        'transition-all duration-200 ease-out motion-reduce:transform-none motion-reduce:opacity-100 motion-reduce:transition-none',
        'md:top-0 md:h-full md:max-h-full md:w-80 md:border-r md:border-t-0',
        presencePhase === 'entered'
          ? 'translate-y-0 opacity-100 md:translate-x-0'
          : 'translate-y-5 opacity-0 md:-translate-x-5 md:translate-y-0'
      )}
      role="complementary"
      aria-label="Image filter panel"
      data-testid="modal-filter-panel"
    >
      <div className="flex-1 space-y-5 overflow-y-auto p-6">
        <div className="flex items-center justify-between border-b border-border pb-4">
          <h3 className="text-lg font-semibold text-foreground">画像加工</h3>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close filter panel">
            ×
          </Button>
        </div>

        <div className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Presets
          </p>
          <div className="grid grid-cols-3 gap-2">
            {IMAGE_FILTER_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className="space-y-2 rounded-lg border border-border bg-background/60 p-2 text-left transition-colors hover:border-primary/60"
                onClick={() => onChange(preset.settings)}
              >
                <img
                  src={previewSrc}
                  alt={preset.label}
                  className="h-16 w-full rounded object-cover"
                  style={{ filter: buildImageCssFilter(preset.settings) }}
                />
                <p className="truncate text-xs font-medium text-foreground">{preset.label}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <Button
            type="button"
            variant={isCropOverlayVisible ? 'default' : 'outline'}
            className="w-full"
            data-testid="crop-overlay-toggle"
            onClick={onToggleCropOverlay}
          >
            {isCropOverlayVisible ? 'トリミングガイドを隠す' : 'トリミングガイドを表示'}
          </Button>

          {FILTER_KEYS.map((key) => {
            const bounds = IMAGE_FILTER_CONTROL_BOUNDS[key];
            const sliderValue = value[key];
            return (
              <label key={key} className="block space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground">{FILTER_LABELS[key]}</span>
                  <span className="font-mono text-muted-foreground">
                    {formatFilterValue(key, sliderValue)}
                  </span>
                </div>
                <input
                  type="range"
                  min={bounds.min}
                  max={bounds.max}
                  step={bounds.step}
                  value={sliderValue}
                  data-testid={`filter-slider-${key}`}
                  onChange={(event) =>
                    onChange({
                      ...value,
                      [key]: Number(event.target.value),
                    })
                  }
                  className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-muted"
                />
              </label>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-2 border-t border-border p-6 pt-4">
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onReset}>
            リセット
          </Button>
          <Button className="flex-1" onClick={onSave} disabled={isSaving}>
            {isSaving ? '保存中...' : 'クラウドに保存'}
          </Button>
        </div>
        {onDownload && (
          <Button variant="outline" className="w-full" onClick={onDownload} disabled={isSaving}>
            ダウンロード
          </Button>
        )}
      </div>
    </aside>
  );
}
